/* Cómo se dice cada cosa de la ficha de una unidad.
 *
 * La ficha no trae ningún dato nuevo: junta cinco que ya estaban repartidos en
 * cinco pantallas. Lo que sí es nuevo, y es lo que vive acá, es el riesgo de
 * juntarlos: **puestos uno al lado del otro, dos hechos de peso distinto se
 * leen igual**. Es la misma trampa que ya resolvió `shared/ubicacion.js` para
 * una celda de tabla, con cuatro bloques más.
 *
 * Tres reglas, y las tres salen de errores que ya cometimos:
 *
 *   1. NADA SE MUESTRA SIN SU FECHA. «Río Grande» solo se lee como *está ahí
 *      ahora* y puede ser de hace tres semanas.
 *   2. «NO HAY» Y «NO PUDE CONSULTAR» NO SE DICEN IGUAL. El primero es un
 *      hueco que se carga; el segundo manda a arreglar algo que no está roto.
 *      Por eso cada bloque tiene su propio estado y no un `null` compartido.
 *   3. LO QUE FALTA SE CUENTA. Un bloque que desaparece cuando no tiene dato
 *      hace creer que esa pregunta no existe.
 *
 *      Desde el 02/10 la ficha esconde el bloque vacío («lo mismo para los
 *      vehículos», Igna, después de la ficha de la persona). La regla de acá
 *      sigue: el vacío se MARCA (`vacio: true`, con su frase) y no se deduce
 *      de un `null`. Es esa marca la que deja esconder un «nunca cruzó» sin
 *      esconder un «no pude consultar».
 *
 * Es puro: entran filas, sale qué mostrar. Se prueba en una tabla.
 */

import { diaAr, enAR, hoyAr } from "./fechaAr";

/* ── El instante, no el día ────────────────────────────────────────────────
 *
 * Un camión entra a la mañana y sale a la tarde: dos cruces con la MISMA
 * fecha. Es el caso normal —74 de los 404 tractores con registro lo tienen— y
 * mostrar sólo el día hace que «entró» y «salió» del mismo martes se lean como
 * el mismo hecho. La hora es lo que los separa.
 */
export function instante(iso, hoy = new Date()) {
  // En hora argentina, no en la de la máquina del que mira: el mismo cruce
  // decía una hora acá y otra en cualquier otro huso (ver fechaAr.js).
  if (!iso) return "";
  const d = enAR(iso);
  if (!d) return "";
  const h = enAR(hoy);
  const p2 = (n) => String(n).padStart(2, "0");
  const dia = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const hoyDia = Date.UTC(h.getUTCFullYear(), h.getUTCMonth(), h.getUTCDate());
  const dias = Math.round((hoyDia - dia) / 86400000);
  const hora = `${p2(d.getUTCHours())}:${p2(d.getUTCMinutes())}`;
  if (dias === 0) return `hoy ${hora}`;
  if (dias === 1) return `ayer ${hora}`;
  const fecha = `${p2(d.getUTCDate())}/${p2(d.getUTCMonth() + 1)}/` +
                String(d.getUTCFullYear()).slice(2);
  return `${fecha} ${hora}`;
}

export function hace(iso, hoy = new Date()) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const dias = Math.floor((hoy - d) / 86400000);
  if (dias < 0) return "";
  if (dias === 0) return "hoy";
  if (dias === 1) return "hace 1 día";
  return `hace ${dias} días`;
}

/* ── EL TALLER ─────────────────────────────────────────────────────────────
 *
 * Dos cosas distintas que la ficha no puede fundir:
 *
 *   · la ESTADÍA — el camión está adentro del taller ahora;
 *   · los TICKETS — qué se le está haciendo.
 *
 * Un camión puede estar adentro sin ticket abierto (entró y todavía nadie
 * cargó el trabajo) y puede tener tickets abiertos estando afuera (salió sin
 * repararse). Fundirlos en un «está en el taller» convertiría el segundo caso
 * en una unidad que figura adentro y no está — y la de portería la dejaría
 * salir creyendo que el sistema se equivocó.
 */
export function taller({ estadia, tickets } = {}) {
  const abiertos = tickets || [];
  return {
    adentro: !!estadia,
    motivo: estadia?.motivo || estadia?.nota || "",
    lugar: estadia?.lugar || "",
    desde: estadia?.entrada || null,
    abiertos,
    /* «Ningún ticket abierto» se DICE, no se omite. Un bloque que desaparece
     * hace que «no tiene trabajo pendiente» y «esta pantalla no mira el
     * taller» se vean igual. */
    vacio: !estadia && abiertos.length === 0,
  };
}

/* ── EL HISTORIAL COMPARTIDO ────────────────────────────────────────────────
 *
 * Una sola línea de tiempo con las dos fuentes, y **cada fila dice de dónde
 * salió**. No son la misma clase de registro y por eso no se dibujan igual:
 *
 *   · el de la **ticketera** tiene un estado que alguien movió —lo tomó, lo
 *     cerró, lo canceló—, un mecánico y una prioridad;
 *   · el de **puerto** es alguien declarando lo que hizo, y se termina ahí.
 *
 * Sin la marca, un trabajo declarado se leería como una reparación con
 * seguimiento: alguien esperaría poder verle el estado y no hay ninguno.
 *
 * Y UN TRABAJO DE PUERTO NO SE DIBUJA COMO «CERRADO». Sería inventarle una
 * máquina de estados que no tiene, y en la lista quedaría indistinguible de un
 * ticket que alguien resolvió.
 */
/* Y desde P-42 hay una TERCERA: los dos talleres viejos del hub. Son 6.468
 * registros en `eventos` que quedaron sin pantalla cuando se sacaron «Taller
 * tractores» y «Taller furgones» — se dijo entonces que los datos no se
 * tocaban, y se cumplió, pero esta ficha nació después y nunca los sumó.
 *
 * Se llama «Taller (histórico)» y no «Taller» a secas: son cargas que ya no
 * se hacen, y sin la marca alguien iría a buscar el ticket que las respalda.
 * Tampoco tienen estado, por el mismo motivo que las de puerto. */
export const FUENTES = {
  ticketera: "Ticketera",
  puerto: "Puerto",
  taller_viejo: "Taller (histórico)",
};

export function historial(filas) {
  return (filas || []).map((f) => ({
    ...f,
    clave: `${f.fuente}-${f.id}`,
    fuenteTexto: FUENTES[f.fuente] || f.fuente,
    /* `estado` es `null` en los de puerto y eso viaja tal cual. Rellenarlo con
     * «hecho» para que las dos columnas coincidan sería exactamente la mentira
     * que la separación de tablas viene a evitar. */
    tieneEstado: Boolean(f.estado),
  }));
}

/* Los tickets abiertos son los que no están cerrados ni cancelados.
 *
 * Se filtra por lo que NO es y no por una lista de lo que sí: la ticketera
 * puede sumar un estado mañana —«esperando repuesto»— y con una lista blanca
 * ese ticket desaparecería de la ficha sin que nadie lo note. Al revés, un
 * estado nuevo aparece de más, que se ve y se corrige.
 */
export const CERRADOS = ["cerrado", "cancelado", "anulado"];

export const estaAbierto = (t) =>
  !CERRADOS.includes(String(t?.estado || "").toLowerCase());

/* ── ADBLUE ────────────────────────────────────────────────────────────────
 *
 * TRES RESPUESTAS Y NO DOS, que es todo el punto del padrón de AdBlue: hay 167
 * unidades confirmadas, y las **86 que faltan no son «no usa»** — son «no está
 * en la fuente». Decir que no tiene el sistema manda a alguien a revisar un
 * tanque que funciona.
 */
export function adblue(fila) {
  if (!fila) {
    return {
      texto: "No sabemos",
      tono: "gris",
      detalle:
        "Esta unidad todavía no reportó nivel de urea ni figura en la ficha del fabricante. No es lo mismo que no tener el sistema.",
    };
  }
  if (fila.tiene === true) {
    return {
      texto: "Usa AdBlue",
      tono: "ok",
      detalle: fila.fuente
        ? `Confirmado por ${fila.fuente}`
        : "Confirmado",
    };
  }
  if (fila.tiene === false) {
    return {
      texto: "No usa",
      tono: "gris",
      detalle: fila.fuente
        ? `Según ${fila.fuente}${fila.detalle ? ` · ${fila.detalle}` : ""}`
        : "Sin sistema SCR",
    };
  }
  return {
    texto: "No sabemos",
    tono: "gris",
    detalle:
      "La unidad está en el padrón de AdBlue pero todavía no se pudo confirmar si tiene el sistema.",
  };
}

/* ── EL ÚLTIMO CRUCE ───────────────────────────────────────────────────────
 *
 * ES EL ÚLTIMO, NO EL HABITUAL, y hay que decirlo en la pantalla porque la
 * frase invita a lo contrario. Los datos: 3,1 tractores distintos por chofer
 * de promedio y **82 de 258 tienen su último cruce con uno que no es el que
 * más usan**. Contestar por el habitual erraría de persona una de cada tres
 * veces, justo el día que alguien pregunta — que es el día en que agarró otro.
 */
const CONTRAPARTE = { tractor: "furgón", furgon: "tractor" };

export function cruce(fila, tipo, hoy = new Date()) {
  if (!fila) {
    return {
      vacio: true,
      titulo: "Nunca cruzó el portón",
      detalle:
        "No hay ningún evento de portería con esta unidad. No es un error: hay unidades sin un solo cruce anotado.",
    };
  }
  const salio = String(fila.tipo || "").toUpperCase() === "SALIDA";
  const con = (fila.con || []).filter(Boolean);
  const que = CONTRAPARTE[tipo] || "unidad";

  return {
    vacio: false,
    titulo: salio ? "Salió" : "Entró",
    tono: salio ? "info" : "gris",
    cuando: instante(fila.instante, hoy),
    hace: hace(fila.instante, hoy),
    /* El DNI se muestra aunque el padrón no tenga el nombre. Son 1 de 379, y
     * un número es más de lo que había: con él se busca a la persona. */
    chofer: fila.chofer || null,
    dni: fila.dni || null,
    legajo: fila.legajo || null,
    /* «Sin chofer anotado» es un hueco VISIBLE, y por eso se dice en vez de
     * dejar el renglón en blanco: el que falta se carga, el que no se ve no. */
    sinChofer: !fila.dni,
    con,
    queEsCon: con.length === 1 ? que : `${que}es`,
  };
}

/* ── EL TURNO DE RTO ───────────────────────────────────────────────────────
 *
 * NUNCA PEGADO AL VENCIMIENTO. Un turno es una INTENCIÓN y una revisión es un
 * HECHO; puestos en el mismo renglón, «le sacamos turno» se lee como «ya tiene
 * el RTO» y el portón deja salir un camión vencido. Va con su propia palabra y
 * en su propia línea, y sólo salen los abiertos.
 */
export function turno(fila) {
  if (!fila) return null;
  return {
    fecha: fila.fecha,
    hora: fila.hora ? String(fila.hora).slice(0, 5) : "",
    planta: fila.planta || "",
    estado: fila.estado || "",
    /* «Pedido» y «confirmado» no son lo mismo y la agenda los distingue: uno
     * es un llamado hecho y el otro una fecha que la planta aceptó. */
    tono: fila.estado === "confirmado" ? "info" : "gris",
  };
}

/* ── Los últimos viajes de la unidad, en formato de lectura ────────────────
 *
 * Los movimientos guardan el lugar como código (`RGD`, `BUE`) — es como los
 * carga tráfico—. Acá se pasan a nombre para leerlos de un vistazo; el que no
 * está en la tabla se muestra tal cual (mejor «LEGUIZAMON» que un hueco). Los
 * nombres largos que ya vienen escritos («RIO GALLEGOS», «MENDOZA») caen en el
 * mismo camino y se muestran como están. */
const LUGARES = {
  RGD: "Río Grande",
  BUE: "Buenos Aires",
  USH: "Ushuaia",
  MC: "Mercado Central",
  "M.CENTRAL": "Mercado Central",
  LOG: "Log. Central",
  "LOG CENTRAL": "Log. Central",
  DEF: "Defibe",
  RGL: "Río Gallegos",
  "RIO GALLEGOS": "Río Gallegos",
  "R.GALLLEGOS": "Río Gallegos",
};

export function lugarBonito(cod) {
  const c = String(cod || "").trim();
  if (!c) return "";
  return LUGARES[c.toUpperCase()] || c;
}

function ddmm(fecha) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(fecha || ""));
  return m ? `${m[3]}/${m[2]}` : "";
}

/* Las filas del RPC `viajes_de_unidad` → lo que dibuja el bloque. Puro. */
export function viajesUnidad(filas) {
  return (filas || []).map((v) => {
    const origen = lugarBonito(v.origen);
    const destino = lugarBonito(v.destino);
    const ruta = [origen, destino].filter(Boolean).join(" → ") || "—";
    return {
      id: v.id,
      fecha: ddmm(v.fecha),
      ruta,
      chofer: v.chofer || "",
      cliente: v.cliente || "",
      finalizado: !!v.finalizado,
    };
  });
}

/* ── SEGÚN SCANIA ──────────────────────────────────────────────────────────
 *
 * La foto de las 08:00 de lo que Scania dice del tractor (`scania_ultima`),
 * puesta al lado de lo que se cargó a mano. Igna, 09/10: «Los km se guardan
 * los dos, no pises lo manual». Las tres reglas de arriba valen doble:
 *
 *   1. LA FECHA VA EN EL TÍTULO. «512.340 km» sin fecha se lee como el
 *      tablero de ahora, y puede ser de hace una semana si el camión no
 *      reportó. Por eso el título es «Según Scania · al dd/mm hh:mm» y la
 *      última señal tiene su propio renglón.
 *   2. LO DE SCANIA NO REEMPLAZA LO MANUAL, SE PONE AL LADO. El chasis del
 *      padrón y el de Scania se muestran los dos cuando no coinciden; el
 *      odómetro de Scania y el anotado en el vale de urea, los dos siempre.
 *      Cuál vale lo decide una persona: acá no se corrige nada.
 *   3. NUNCA UN CERO. Un odómetro o unas horas que Scania no informó no se
 *      dibujan, y una unidad que Scania no ve no tiene tarjeta: «0 km» se lee
 *      como un camión nuevo.
 *
 * Es puro: entra lo que devolvió `scaniaDe` y la fila del padrón, sale qué
 * mostrar.
 */

/* El chasis como lo compara el bot (`scania_relevamiento.normalizar_chasis`):
 * sólo letras y números; de un VIN de 17, los últimos 7 dígitos; si son 7
 * dígitos, ésos. LO QUE NO ES NINGUNA DE LAS DOS NO SE ADIVINA: «757» (el
 * interno metido en la celda) o «54095916» devuelven `null`, y entonces no
 * coinciden con nada — que es lo cierto. */
export function chasisNormal(valor) {
  const s = String(valor ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (s.length === 17 && /^\d{7}$/.test(s.slice(-7))) return s.slice(-7);
  if (/^\d{7}$/.test(s)) return s;
  return null;
}

const numeroOnull = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const miles = (n) => Math.round(n).toLocaleString("es-AR");
export const kmTexto = (n) => `${miles(n)} km`;

/* Un camión no hace más de esto por día, ni con dos choferes: Buenos Aires →
 * Río Grande son unos 3.000 km y se hacen en tres días. Es el techo para decir
 * que dos odómetros NO CIERRAN, no una estimación: se elige alto a propósito,
 * para que sólo salte lo imposible (un dígito de menos en el vale, un
 * odómetro de otro camión) y no un viaje largo. */
export const KM_POR_DIA_MAX = 2000;
/* Lo que se le perdona a un vale escrito a mano: el número de ayer, un
 * redondeo. Para atrás más que esto, el odómetro no va. */
export const KM_TOLERANCIA = 500;

const DIA_MS = 86400000;

/* ¿Dos lecturas del odómetro (`{ km, cuando }`) se contradicen? Se ordenan
 * por fecha: la de después no puede ser menor que la de antes, ni haber
 * sumado más de lo que un camión puede andar en ese tiempo. `null` si cierran
 * o si falta algo para comparar. */
export function kmNoCierran(a, b) {
  const ta = a?.cuando ? new Date(a.cuando).getTime() : NaN;
  const tb = b?.cuando ? new Date(b.cuando).getTime() : NaN;
  if (!Number.isFinite(a?.km) || !Number.isFinite(b?.km)) return null;
  if (!Number.isFinite(ta) || !Number.isFinite(tb)) return null;
  const [antes, despues] = ta <= tb ? [a, b] : [b, a];
  const dias = Math.abs(tb - ta) / DIA_MS;
  const delta = despues.km - antes.km;
  const cuanto =
    dias < 1 ? "en menos de un día" : `en ${Math.round(dias)} ${Math.round(dias) === 1 ? "día" : "días"}`;
  if (delta < -KM_TOLERANCIA) {
    return {
      delta,
      dias,
      texto: `No cierran: la lectura más nueva tiene ${kmTexto(-delta)} MENOS que la anterior, y un odómetro no va para atrás. Alguna de las dos está mal; no se corrige ninguna sola.`,
    };
  }
  if (delta > KM_POR_DIA_MAX * Math.max(dias, 1) + KM_TOLERANCIA) {
    return {
      delta,
      dias,
      texto: `No cierran: entre una lectura y la otra hay ${kmTexto(delta)} ${cuanto}, más de lo que anda un camión. Alguna de las dos está mal; no se corrige ninguna sola.`,
    };
  }
  return null;
}

const FUENTE_SERVICE = {
  service_planning: "Service Planning",
  rfms: "rFMS",
};

/* «dd/mm hh:mm» en hora argentina. */
function diaHoraCorta(iso) {
  const d = enAR(iso);
  if (!d) return "";
  const p2 = (n) => String(n).padStart(2, "0");
  return `${p2(d.getUTCDate())}/${p2(d.getUTCMonth() + 1)} ` +
         `${p2(d.getUTCHours())}:${p2(d.getUTCMinutes())}`;
}

/* El relevamiento corre a las 08:00 y reintenta hasta las 08:45. Antes de las
 * 09:00 lo último que puede haber es lo de AYER, y eso no es viejo: decirlo
 * pintaría de «atrasada» a toda la flota cada mañana. */
const HORA_DEL_RELEVAMIENTO = 9;

function diaEsperado(hoy) {
  const h = enAR(hoy);
  const base = h.getUTCHours() >= HORA_DEL_RELEVAMIENTO ? hoy : new Date(hoy.getTime() - DIA_MS);
  return hoyAr(base);
}

export const TITULO_SCANIA = "Según Scania";

export function scania(dato, filaPadron, hoy = new Date()) {
  if (!dato || dato.vista === false) {
    return {
      vacio: true,
      titulo: TITULO_SCANIA,
      detalle: "El relevamiento diario de Scania todavía no está en la base.",
    };
  }
  const l = dato.lectura;
  if (!l) {
    return {
      vacio: true,
      titulo: TITULO_SCANIA,
      detalle: "Scania no ve esta unidad.",
    };
  }

  const al = diaHoraCorta(l.capturado_en) || diaAr(l.fecha).slice(0, 5);
  const titulo = al ? `${TITULO_SCANIA} · al ${al}` : TITULO_SCANIA;
  const fechaLectura = String(l.fecha || "").slice(0, 10);
  const viejo =
    fechaLectura && fechaLectura < diaEsperado(hoy)
      ? `último dato de Scania: ${diaAr(fechaLectura).slice(0, 5)}`
      : null;

  /* ── El odómetro: el de Scania y el anotado, los dos ── */
  const kmScania = numeroOnull(l.odometro_km);
  const odometro = kmScania !== null && kmScania > 0 ? kmScania : null;
  const kmVale = numeroOnull(dato.vale?.km);
  const vale = kmVale !== null && kmVale > 0
    ? { km: kmVale, texto: kmTexto(kmVale), cuando: dato.vale.fecha, dia: diaAr(dato.vale.fecha).slice(0, 5) }
    : null;
  const cuandoScania = l.ultima_senal || l.capturado_en || null;
  const noCierran =
    odometro !== null && vale
      ? kmNoCierran({ km: odometro, cuando: cuandoScania }, { km: vale.km, cuando: vale.cuando })
      : null;
  const km =
    odometro === null && !vale && !dato.valeFallo
      ? null
      : {
          scania: odometro !== null ? kmTexto(odometro) : null,
          vale,
          valeFallo: Boolean(dato.valeFallo),
          noCierran: noCierran ? noCierran.texto : null,
        };

  const horas = numeroOnull(l.horas_motor);

  /* ── El próximo service ── */
  const sk = numeroOnull(l.service_km);
  const so = numeroOnull(l.service_objetivo_km);
  let service = null;
  if (sk !== null || so !== null || l.service_fecha) {
    const partes = [];
    if (sk !== null) {
      partes.push(
        sk < 0 ? `se pasó por ${kmTexto(-sk)}` : sk === 0 ? "le toca ya" : `faltan ${kmTexto(sk)}`,
      );
    } else if (so !== null) {
      partes.push(`a los ${kmTexto(so)}`);
    }
    if (l.service_fecha) partes.push(`el ${diaAr(l.service_fecha)}`);
    service = {
      texto: partes.join(" · "),
      pasado: sk !== null && sk < 0,
      fuente: FUENTE_SERVICE[l.service_fuente] || null,
    };
  }

  /* ── La última señal: de cuándo son los números ── */
  let senal = null;
  if (l.ultima_senal) {
    const ts = new Date(l.ultima_senal).getTime();
    const tc = new Date(l.capturado_en || hoy).getTime();
    senal = {
      cuando: instante(l.ultima_senal, hoy),
      hace: hace(l.ultima_senal, hoy),
      vieja: Number.isFinite(ts) && Number.isFinite(tc) && tc - ts > DIA_MS,
    };
  }

  /* ── El chasis: el de Scania al lado del del padrón ──
   *
   * Sin la fila del padrón —la unidad no está, o el padrón todavía no llegó
   * del realtime— no se compara: «el padrón no lo tiene cargado» sería una
   * afirmación sobre una fila que no se leyó. */
  const deScania = chasisNormal(l.chasis);
  const crudo = String(filaPadron?.chasis ?? "").trim();
  const enPadron = crudo && crudo !== "0" ? crudo : "";
  let chasis = null;
  if (deScania) {
    chasis = {
      scania: deScania,
      padron: enPadron,
      estado: !filaPadron
        ? "sin_padron"
        : !enPadron
          ? "falta"
          : chasisNormal(enPadron) === deScania
            ? "coincide"
            : "difiere",
    };
  }

  const equipo = [
    l.equipo_telematico,
    l.en_rfms && l.norma ? String(l.norma).replace(/_/g, " ") : null,
    l.en_rfms ? l.modelo_rfms : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const notas = [];
  if (l.dado_de_baja)
    notas.push(
      "Este equipo figura dado de baja en Scania: puede ser el camión que tenía antes este interno.",
    );
  if (l.interno_fuente === "dominio_padron")
    notas.push(
      "Scania lo reconoce sólo por la patente, que a veces trae la de otra unidad: si el chasis no coincide, puede ser otro camión.",
    );

  return {
    vacio: false,
    titulo,
    viejo,
    km,
    horas: horas !== null && horas > 0 ? `${miles(horas)} h` : null,
    service,
    senal,
    chasis,
    vin: l.vin || null,
    equipo: equipo || null,
    notas,
  };
}
